import React, { useEffect, useState, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertTriangle, Zap, Lock, Download, ShieldCheck, Smartphone, DollarSign, Gift, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { SalesPopup } from './SalesPopup';
import { ExitUpsellPopup } from './ExitUpsellPopup';
// --- Meta Pixel Helper ---
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
const initMetaPixel = () => {
  if (typeof window === 'undefined') return;
  (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    } else {
      b.head.appendChild(t);
    }
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', '898349736246164');
  window.fbq('track', 'PageView');
};
const trackAddToCart = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      value: 6.36,
      currency: 'USD',
      content_name: 'Faceless Cash: El Método de Clonación Viral'
    });
  }
};
const goToCheckout = () => {
  trackAddToCart();
  window.open('https://www.factoravance.com/cart/51548141486400:1', '_blank');
};
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const pulseAnimation = {
  scale: [1, 1.03, 1],
  transition: {
    repeat: Infinity,
    duration: 2
  }
};
// --- Components ---
const UrgencyBar = () => <motion.div initial={{
  y: -50
}} animate={{
  y: 0
}} className="fixed top-0 left-0 w-full z-50 bg-[#ff3d3d] text-white py-2 px-4 text-center font-bold text-sm md:text-base shadow-lg" data-id="element-43">
    <motion.div animate={{
    opacity: [1, 0.7, 1]
  }} transition={{
    repeat: Infinity,
    duration: 1.5
  }} className="flex items-center justify-center gap-2" data-id="element-44">
      <div className="flex gap-1" data-id="element-45">
        <AlertTriangle size={16} fill="white" data-id="element-46" />
        <Zap size={16} fill="white" data-id="element-47" />
      </div>
      <span data-id="element-48">
        OFERTA ESPECIAL: Sistema + 3 Bonos por solo $6.36 USD — Solo mientras
        estás en esta página
      </span>
    </motion.div>
  </motion.div>;
const HeroSection = () => <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 px-4 overflow-hidden flex flex-col justify-center items-center text-center bg-[#080808]" data-id="element-49">
    {/* Background Texture */}
    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
    backgroundImage: 'radial-gradient(#333 1px, transparent 1px)',
    backgroundSize: '30px 30px'
  }} data-id="element-50" />

    <div className="max-w-3xl mx-auto relative z-10" data-id="element-51">
      <motion.h1 initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4 tracking-tight" data-id="element-52">
        OFERTA ÚNICA: El Sistema Faceless + 3 Bonos por solo $6.36 USD
      </motion.h1>

      <motion.p initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-lg md:text-xl text-[#f5a623] italic mb-6" data-id="element-53">
        (Disponible solo mientras esta página esté abierta)
      </motion.p>

      <motion.p initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto font-light leading-relaxed mb-8" data-id="element-54">
        Tu cuenta de ingresos "sin rostro" está a un solo clic. Sin cámaras, sin
        bailar frente a la pantalla y sin sacrificar tu privacidad.
      </motion.p>

      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} data-id="element-55">
        <motion.button animate={pulseAnimation} onClick={() => document.getElementById('offer-summary')?.scrollIntoView({
        behavior: 'smooth'
      })} className="w-full md:w-auto bg-[#00e676] hover:bg-[#00c853] text-black font-black text-xl md:text-2xl py-4 px-8 md:px-12 rounded-xl shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all transform hover:-translate-y-1 border-b-4 border-[#00b248] cursor-pointer" data-id="element-56">
          SÍ, QUIERO MI OFERTA →
        </motion.button>
      </motion.div>
    </div>
  </section>;
const PainPointSection = () => <section className="py-12 px-4 bg-[#0d0d0d]" data-id="element-57">
    <div className="max-w-3xl mx-auto text-center" data-id="element-58">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={staggerContainer} className="space-y-6" data-id="element-59">
        <motion.h3 variants={fadeInUp} className="text-2xl md:text-3xl text-white font-bold" data-id="element-60">
          ¿Por qué la mayoría falla al intentar crear contenido?
        </motion.h3>
        <motion.p variants={fadeInUp} className="text-lg md:text-xl text-gray-300 leading-relaxed" data-id="element-61">
          No es falta de ganas, es{' '}
          <span className="text-white font-bold" data-id="element-62">exposición y parálisis</span>.
        </motion.p>
        <motion.p variants={fadeInUp} className="text-base md:text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto" data-id="element-63">
          Si te frena la vergüenza, la falta de tiempo o no saber qué decir, has
          llegado al lugar correcto. No necesitas ser influencer. Necesitas un{' '}
          <span className="text-white font-bold" data-id="element-64">
            Sistema de Producción Invisible
          </span>{' '}
          que trabaje mientras tú vives tu vida.
        </motion.p>
      </motion.div>
    </div>
  </section>;
const OfferIntroSection = () => <section className="pt-16 pb-8 px-4 bg-[#080808]" data-id="element-65">
    <div className="max-w-3xl mx-auto text-center" data-id="element-66">
      <motion.h3 initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-2xl md:text-3xl text-white font-bold" data-id="element-67">
        Lo que vas a recibir hoy:
      </motion.h3>
    </div>
  </section>;
const MainProductCard = () => <section className="py-8 px-4 bg-[#080808]" data-id="element-68">
    <div className="max-w-3xl mx-auto" data-id="element-69">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="bg-[#111] border-2 border-[#f5a623] rounded-2xl p-6 md:p-8 relative overflow-hidden shadow-[0_0_30px_rgba(245,166,35,0.1)]" data-id="element-70">
        <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-71">
          <div className="flex-1" data-id="element-72">
            <div className="flex items-center gap-2 text-[#00e676] font-bold mb-2 text-sm tracking-wider uppercase" data-id="element-73">
              <Check size={16} data-id="element-74" /> PRODUCTO PRINCIPAL
            </div>
            <h3 className="text-3xl md:text-4xl text-white font-bold mb-3" data-id="element-75">
              Sistema Faceless Cash
            </h3>
            <p className="text-gray-300 mb-4 leading-relaxed text-sm md:text-base" data-id="element-76">
              El mapa exacto para construir una cuenta en TikTok que facture en
              USD sin que nadie sepa quién eres.
            </p>
            <ul className="space-y-3 mb-6" data-id="element-77">
              <li className="flex items-start gap-2 text-gray-300 text-sm md:text-base" data-id="element-78">
                <Check size={18} className="text-[#00e676] shrink-0 mt-0.5" data-id="element-79" />
                <span data-id="element-80">Estrategia de IA para guiones y edición rápida.</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm md:text-base" data-id="element-81">
                <Check size={18} className="text-[#00e676] shrink-0 mt-0.5" data-id="element-82" />
                <span data-id="element-83">Nichos de alto CPM (los que mejor pagan en USA).</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm md:text-base" data-id="element-84">
                <Check size={18} className="text-[#00e676] shrink-0 mt-0.5" data-id="element-85" />
                <span data-id="element-86">Monetización desde el día 1.</span>
              </li>
            </ul>
            <div className="text-gray-500 text-sm italic" data-id="element-87">
              (Valor real: $47.00 USD)
            </div>
          </div>
          <div className="w-full md:w-1/4 flex justify-center shrink-0" data-id="element-88">
            <Package size={80} className="text-[#f5a623] opacity-80" strokeWidth={1} data-id="element-89" />
          </div>
        </div>
      </motion.div>
    </div>
  </section>;
const BonusesSection = () => <section className="py-8 px-4 bg-[#0d0d0d]" data-id="element-90">
    <div className="max-w-3xl mx-auto" data-id="element-91">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="flex items-center gap-3 mb-8 justify-center md:justify-start" data-id="element-92">
        <Gift size={28} className="text-[#00e676]" data-id="element-93" />
        <h3 className="text-2xl md:text-3xl text-white font-bold" data-id="element-94">
          3 BONOS DE ACCIÓN INMEDIATA{' '}
          <span className="text-[#00e676]" data-id="element-95">(INCLUIDOS GRATIS):</span>
        </h3>
      </motion.div>

      <div className="space-y-6" data-id="element-96">
        {/* Bonus 1 */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-[#111] border border-white/10 rounded-xl overflow-hidden" data-id="element-97">
          <div className="flex flex-col md:flex-row" data-id="element-98">
            <div className="w-full md:w-[35%] bg-[#080808] border-b md:border-b-0 md:border-r border-white/5" data-id="element-99">
              <img src="https://cdn.magicpatterns.com/uploads/gF4WHYtUfB5WARp4pjqLuF/bonus1_img.png" alt="Kit Cero a 1 Video" className="w-full h-48 md:h-full object-cover" data-id="element-100" />
            </div>
            <div className="w-full md:w-[65%] p-6" data-id="element-101">
              <h4 className="text-xl text-white font-bold mb-1" data-id="element-102">
                1. Kit "De 0 a tu 1er Video"
              </h4>
              <p className="text-[#00e676] text-sm font-bold mb-3" data-id="element-103">
                (Valor: $27.00 USD)
              </p>
              <p className="text-gray-300 text-sm leading-relaxed" data-id="element-104">
                La cura definitiva contra la parálisis. Te entrego{' '}
                <span className="text-white font-bold" data-id="element-105">
                  5 guiones armados, prompts de voz IA y visuales listos
                </span>
                . Tu primer video estará al aire en menos de 30 minutos.{' '}
                <span className="text-white font-bold" data-id="element-106">Garantizado</span>.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bonus 2 */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-[#111] border border-white/10 rounded-xl overflow-hidden" data-id="element-107">
          <div className="flex flex-col md:flex-row" data-id="element-108">
            <div className="w-full md:w-[35%] bg-[#080808] border-b md:border-b-0 md:border-r border-white/5" data-id="element-109">
              <img src="https://cdn.magicpatterns.com/uploads/7L6Eqi1RzBpjheXt3knAAV/bonus2_img.png" alt="Banco de Hooks" className="w-full h-48 md:h-full object-cover" data-id="element-110" />
            </div>
            <div className="w-full md:w-[65%] p-6" data-id="element-111">
              <h4 className="text-xl text-white font-bold mb-1" data-id="element-112">
                2. Banco de Hooks "Detén el Scroll"
              </h4>
              <p className="text-[#00e676] text-sm font-bold mb-3" data-id="element-113">
                (Valor: $37.00 USD)
              </p>
              <p className="text-gray-300 text-sm leading-relaxed" data-id="element-114">
                Si no detienes el dedo del espectador en 2 segundos, tu video
                muere. Te doy{' '}
                <span className="text-white font-bold" data-id="element-115">
                  50 ganchos probados
                </span>{' '}
                para copiar y pegar. Cambia 2 palabras y mira cómo{' '}
                <span className="text-white font-bold" data-id="element-116">
                  explota tu retención
                </span>
                .
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bonus 3 */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-[#111] border border-white/10 rounded-xl overflow-hidden" data-id="element-117">
          <div className="flex flex-col md:flex-row" data-id="element-118">
            <div className="w-full md:w-[35%] bg-[#080808] border-b md:border-b-0 md:border-r border-white/5" data-id="element-119">
              <img src="https://cdn.magicpatterns.com/uploads/rpT3LXyof29uYssGbsDuoh/bonus3_img.png" alt="Checklist Anti-Ban" className="w-full h-48 md:h-full object-cover" data-id="element-120" />
            </div>
            <div className="w-full md:w-[65%] p-6" data-id="element-121">
              <h4 className="text-xl text-white font-bold mb-1" data-id="element-122">
                3. Checklist "Modo Seguro" Anti-Ban
              </h4>
              <p className="text-[#00e676] text-sm font-bold mb-3" data-id="element-123">
                (Valor: $17.00 USD)
              </p>
              <p className="text-gray-300 text-sm leading-relaxed" data-id="element-124">
                Publica con cero ansiedad. Mi sistema de semáforo
                (Verde/Amarillo/Rojo) te dice exactamente qué contenido es
                seguro y cómo proteger tu cuenta desde el primer segundo.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
const SummarySection = () => {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const totalPrice = (6.36 + (bump1 ? 9 : 0) + (bump2 ? 9 : 0)).toFixed(2);
  const getDynamicCheckoutUrl = () => {
    if (bump1 && bump2) {
      return 'https://factoravance.com/cart/51581641818432:1';
    }
    if (bump1 && !bump2) {
      return 'https://factoravance.com/cart/51581593420096:1';
    }
    if (!bump1 && bump2) {
      return 'https://factoravance.com/cart/51581649912128:1';
    }
    return 'https://factoravance.com/cart/51548141486400:1';
  };
  const handleDynamicCheckout = () => {
    if (!bump1 && !bump2) {
      setShowUpsell(true);
      return;
    }
    trackAddToCart();
    window.open(getDynamicCheckoutUrl(), '_blank');
  };
  const handleAcceptUpsell = () => {
    trackAddToCart();
    window.open('https://factoravance.com/cart/51595353129280:1', '_blank');
    setShowUpsell(false);
  };
  const handleDeclineUpsell = () => {
    trackAddToCart();
    window.open('https://factoravance.com/cart/51548141486400:1', '_blank');
    setShowUpsell(false);
  };
  return <section id="offer-summary" className="py-12 px-4 bg-[#080808]" data-id="element-125">
      <div className="max-w-3xl mx-auto" data-id="element-126">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-[#151515] border border-[#f5a623]/30 rounded-2xl overflow-hidden shadow-2xl" data-id="element-127">
          <div className="bg-[#1a1a1a] p-5 border-b border-white/10 text-center" data-id="element-128">
            <h3 className="text-2xl text-white font-bold" data-id="element-129">
              Toda la maquinaria por el precio de un café:
            </h3>
          </div>

          <div className="p-6 space-y-4" data-id="element-130">
            <div className="flex items-center justify-between border-b border-white/5 pb-3" data-id="element-131">
              <div className="flex items-center gap-2" data-id="element-132">
                <Check className="text-[#00e676]" size={18} data-id="element-133" />
                <span className="text-gray-300 text-sm md:text-base" data-id="element-134">
                  Sistema Faceless Cash:
                </span>
              </div>
              <span className="text-gray-500 line-through text-sm md:text-base" data-id="element-135">
                $47.00
              </span>
            </div>

            <div className="flex items-center justify-between border-b border-white/5 pb-3" data-id="element-136">
              <div className="flex items-center gap-2" data-id="element-137">
                <Gift className="text-[#f5a623]" size={18} data-id="element-138" />
                <span className="text-gray-300 text-sm md:text-base" data-id="element-139">
                  Bono 1: Kit de Ejecución:
                </span>
              </div>
              <span className="text-gray-500 line-through text-sm md:text-base" data-id="element-140">
                $27.00
              </span>
            </div>

            <div className="flex items-center justify-between border-b border-white/5 pb-3" data-id="element-141">
              <div className="flex items-center gap-2" data-id="element-142">
                <Gift className="text-[#f5a623]" size={18} data-id="element-143" />
                <span className="text-gray-300 text-sm md:text-base" data-id="element-144">
                  Bono 2: 50 Hooks Virales:
                </span>
              </div>
              <span className="text-gray-500 line-through text-sm md:text-base" data-id="element-145">
                $37.00
              </span>
            </div>

            <div className="flex items-center justify-between border-b border-white/5 pb-3" data-id="element-146">
              <div className="flex items-center gap-2" data-id="element-147">
                <Gift className="text-[#f5a623]" size={18} data-id="element-148" />
                <span className="text-gray-300 text-sm md:text-base" data-id="element-149">
                  Bono 3: Setup de Seguridad:
                </span>
              </div>
              <span className="text-gray-500 line-through text-sm md:text-base" data-id="element-150">
                $17.00
              </span>
            </div>

            <div className="flex items-center justify-between pt-2 pb-4" data-id="element-151">
              <div className="flex items-center gap-2" data-id="element-152">
                <DollarSign className="text-white" size={18} data-id="element-153" />
                <span className="text-white font-bold" data-id="element-154">VALOR TOTAL:</span>
              </div>
              <span className="text-red-500 font-bold line-through decoration-red-500" data-id="element-155">
                $128.00 USD
              </span>
            </div>

            <div className="bg-[#00e676]/10 rounded-xl p-5 text-center border border-[#00e676]/30 mt-2" data-id="element-156">
              <h2 className="text-white font-bold text-2xl md:text-3xl mb-1" data-id="element-157">
                ACCEDE HOY POR SOLO:{' '}
                <motion.span key={totalPrice} initial={{
                scale: 1.2,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} className="text-[#00e676] inline-block" data-id="element-158">
                  ${totalPrice} USD
                </motion.span>
              </h2>
              <p className="text-gray-400 text-sm italic" data-id="element-159">
                (Un solo pago. Sin suscripciones. Sin letras chiquitas.)
              </p>
            </div>

            <div className="pt-6 text-center" data-id="element-160">
              <motion.button animate={pulseAnimation} onClick={handleDynamicCheckout} className="w-full bg-[#f5a623] hover:bg-[#e09612] text-black font-black text-xl md:text-2xl py-4 px-8 rounded-xl shadow-[0_0_30px_rgba(245,166,35,0.4)] transition-all transform hover:-translate-y-1 border-b-4 border-[#c4850f] cursor-pointer" data-id="element-161">
                SÍ, QUIERO MI OFERTA COMPLETA POR ${totalPrice}
              </motion.button>
              <div className="flex justify-center gap-4 mt-3 text-xs text-gray-500" data-id="element-162">
                <span className="flex items-center gap-1" data-id="element-163">
                  <Lock size={12} data-id="element-164" /> Pago Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-165">
                  <Zap size={12} data-id="element-166" /> Acceso Inmediato
                </span>
              </div>
            </div>

            {/* Order Bumps */}
            <div className="pt-6 space-y-4" data-id="element-167">
              <div className="flex items-center justify-center gap-2 mb-2" data-id="element-168">
                <Zap size={18} className="text-[#f5a623]" data-id="element-169" />
                <h4 className="text-[#f5a623] font-bold text-lg tracking-wide uppercase" data-id="element-170">
                  Altamente Recomendado
                </h4>
                <Zap size={18} className="text-[#f5a623]" data-id="element-171" />
              </div>

              {/* Order Bump #1 */}
              <motion.div animate={{
              boxShadow: bump1 ? '0 0 20px rgba(0,230,118,0.15)' : ['0 0 0px rgba(0,230,118,0)', '0 0 12px rgba(0,230,118,0.12)', '0 0 0px rgba(0,230,118,0)']
            }} transition={bump1 ? {
              duration: 0.3
            } : {
              repeat: Infinity,
              duration: 3,
              ease: 'easeInOut'
            }} onClick={() => setBump1(!bump1)} className={`rounded-xl border-2 overflow-hidden cursor-pointer transition-colors duration-300 ${bump1 ? 'border-[#00e676]' : 'border-white/10 hover:border-white/20'}`} data-id="element-172">
                <div className={`px-4 py-3 flex items-center gap-3 ${bump1 ? 'bg-[#00e676]/10' : 'bg-[#1a1a1a]'}`} data-id="element-173">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${bump1 ? 'bg-[#00e676] border-[#00e676]' : 'border-gray-500'}`} data-id="element-174">
                    {bump1 && <Check size={14} className="text-black" strokeWidth={3} data-id="element-175" />}
                  </div>
                  <div className="flex-1" data-id="element-176">
                    <p className="text-[#00e676] font-bold text-sm" data-id="element-177">
                      USD Cash-Flow: Nichos de Alta Retención
                    </p>
                  </div>
                  <span className="text-[#00e676] font-bold text-lg" data-id="element-178">+$9</span>
                </div>
                <div className="flex flex-col md:flex-row" data-id="element-179">
                  <div className="w-full md:w-[30%] bg-[#080808]" data-id="element-180">
                    <img src="https://cdn.magicpatterns.com/uploads/4VKSZnPqHnnpov4V6sYhjA/oder1.png" alt="USD Cash-Flow: Nichos de Alta Retención" className="w-full h-40 md:h-full object-cover" data-id="element-181" />
                  </div>
                  <div className="w-full md:w-[70%] p-4 md:p-5" data-id="element-182">
                    <h4 className="text-white font-bold text-base md:text-lg mb-2 leading-snug" data-id="element-183">
                      ¿Quieres saber dónde está el dinero real? Accede a los
                      "Nichos de Oro" por solo $9
                    </h4>
                    <p className="text-gray-400 text-xs md:text-sm leading-relaxed" data-id="element-184">
                      No pierdas meses publicando contenido que paga centavos.
                      Con este recurso, irás directo a donde los anunciantes en
                      USA pagan más dinero (CPM Alto). Te entrego la lista de
                      los{' '}
                      <span className="text-white font-medium" data-id="element-185">
                        5 nichos que pagan entre $15 y $40 USD por cada mil
                        vistas
                      </span>
                      , junto con mi estrategia de "Shadow-Targeting" para
                      forzar al algoritmo a mostrarte solo a personas con alto
                      poder adquisitivo en Estados Unidos.
                    </p>
                    <p className="text-gray-500 text-xs mt-2 italic" data-id="element-186">
                      Ahorra tiempo y asegura que cada video que publiques esté
                      diseñado para maximizar tus ingresos en dólares.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Order Bump #2 */}
              <motion.div animate={{
              boxShadow: bump2 ? '0 0 20px rgba(0,230,118,0.15)' : ['0 0 0px rgba(0,230,118,0)', '0 0 12px rgba(0,230,118,0.12)', '0 0 0px rgba(0,230,118,0)']
            }} transition={bump2 ? {
              duration: 0.3
            } : {
              repeat: Infinity,
              duration: 3,
              ease: 'easeInOut',
              delay: 1.5
            }} onClick={() => setBump2(!bump2)} className={`rounded-xl border-2 overflow-hidden cursor-pointer transition-colors duration-300 ${bump2 ? 'border-[#00e676]' : 'border-white/10 hover:border-white/20'}`} data-id="element-187">
                <div className={`px-4 py-3 flex items-center gap-3 ${bump2 ? 'bg-[#00e676]/10' : 'bg-[#1a1a1a]'}`} data-id="element-188">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${bump2 ? 'bg-[#00e676] border-[#00e676]' : 'border-gray-500'}`} data-id="element-189">
                    {bump2 && <Check size={14} className="text-black" strokeWidth={3} data-id="element-190" />}
                  </div>
                  <div className="flex-1" data-id="element-191">
                    <p className="text-[#00e676] font-bold text-sm" data-id="element-192">
                      SDR - Scripting de Respuesta Directa
                    </p>
                  </div>
                  <span className="text-[#00e676] font-bold text-lg" data-id="element-193">+$9</span>
                </div>
                <div className="flex flex-col md:flex-row" data-id="element-194">
                  <div className="w-full md:w-[30%] bg-[#080808]" data-id="element-195">
                    <img src="https://cdn.magicpatterns.com/uploads/8ngrRcqEUNEXgbEaNMCvro/oder2.png" alt="SDR - Scripting de Respuesta Directa" className="w-full h-40 md:h-full object-cover" data-id="element-196" />
                  </div>
                  <div className="w-full md:w-[70%] p-4 md:p-5" data-id="element-197">
                    <h4 className="text-white font-bold text-base md:text-lg mb-2 leading-snug" data-id="element-198">
                      Deja de buscar "Likes" y empieza a generar "Ventas" por
                      solo $9 adicionales
                    </h4>
                    <p className="text-gray-400 text-xs md:text-sm leading-relaxed" data-id="element-199">
                      La viralidad sin ventas es solo ego. Con este pack de
                      Scripts de Respuesta Directa, aprenderás la psicología
                      exacta para convertir espectadores en compradores en menos
                      de 45 segundos. Recibe{' '}
                      <span className="text-white font-medium" data-id="element-200">
                        20 plantillas de guiones "Copy/Paste"
                      </span>{' '}
                      diseñadas bajo el framework AIDA (Atención, Interés,
                      Deseo, Acción) y mis{' '}
                      <span className="text-white font-medium" data-id="element-201">
                        10 Hooks de Venta Invisible
                      </span>{' '}
                      que activan el deseo de compra sin que parezca que estás
                      vendiendo.
                    </p>
                    <p className="text-gray-500 text-xs mt-2 italic" data-id="element-202">
                      Convierte tu cuenta anónima en una máquina de ventas 24/7
                      sin sonar como una vendedora pesada.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Dynamic Order Breakdown */}
            <div className="pt-6" data-id="element-203">
              <div className="bg-[#111] rounded-xl border border-white/10 overflow-hidden" data-id="element-204">
                <div className="px-5 py-3 bg-[#1a1a1a] border-b border-white/10" data-id="element-205">
                  <h4 className="text-white font-bold text-sm uppercase tracking-wider" data-id="element-206">
                    Tu pedido:
                  </h4>
                </div>
                <div className="px-5 py-4 space-y-3" data-id="element-207">
                  {/* Always included: base product */}
                  <div className="flex items-center justify-between" data-id="element-208">
                    <div className="flex items-center gap-2" data-id="element-209">
                      <Check className="text-[#00e676] shrink-0" size={16} data-id="element-210" />
                      <span className="text-gray-300 text-sm" data-id="element-211">
                        Sistema Faceless Cash + 3 Bonos
                      </span>
                    </div>
                    <span className="text-white font-semibold text-sm" data-id="element-212">
                      $6.36
                    </span>
                  </div>

                  {/* Bump 1 - conditional */}
                  <AnimatePresence data-id="element-213">
                    {bump1 && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.25
                  }} className="overflow-hidden" data-id="element-214">
                        <div className="flex items-center justify-between" data-id="element-215">
                          <div className="flex items-center gap-2" data-id="element-216">
                            <Check className="text-[#00e676] shrink-0" size={16} data-id="element-217" />
                            <span className="text-gray-300 text-sm" data-id="element-218">
                              USD Cash-Flow: Nichos de Alta Retención
                            </span>
                          </div>
                          <span className="text-white font-semibold text-sm" data-id="element-219">
                            $9.00
                          </span>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Bump 2 - conditional */}
                  <AnimatePresence data-id="element-220">
                    {bump2 && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.25
                  }} className="overflow-hidden" data-id="element-221">
                        <div className="flex items-center justify-between" data-id="element-222">
                          <div className="flex items-center gap-2" data-id="element-223">
                            <Check className="text-[#00e676] shrink-0" size={16} data-id="element-224" />
                            <span className="text-gray-300 text-sm" data-id="element-225">
                              SDR - Scripting de Respuesta Directa
                            </span>
                          </div>
                          <span className="text-white font-semibold text-sm" data-id="element-226">
                            $9.00
                          </span>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Divider + Total */}
                  <div className="border-t border-white/10 pt-3 mt-3" data-id="element-227">
                    <div className="flex items-center justify-between" data-id="element-228">
                      <span className="text-white font-bold text-base" data-id="element-229">
                        TOTAL A PAGAR:
                      </span>
                      <motion.span key={totalPrice} initial={{
                      scale: 1.15,
                      opacity: 0
                    }} animate={{
                      scale: 1,
                      opacity: 1
                    }} transition={{
                      duration: 0.3
                    }} className="text-[#00e676] font-black text-xl" data-id="element-230">
                        ${totalPrice} USD
                      </motion.span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="pt-6 text-center" data-id="element-231">
              <motion.button animate={pulseAnimation} onClick={handleDynamicCheckout} className="w-full bg-[#00e676] hover:bg-[#00c853] text-black font-black text-xl md:text-2xl py-4 px-8 rounded-xl shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all transform hover:-translate-y-1 border-b-4 border-[#00b248] cursor-pointer" data-id="element-232">
                COMPLETAR MI COMPRA — ${totalPrice} USD
              </motion.button>
              <div className="flex justify-center gap-4 mt-3 text-xs text-gray-500" data-id="element-233">
                <span className="flex items-center gap-1" data-id="element-234">
                  <Lock size={12} data-id="element-235" /> Pago 100% Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-236">
                  <Zap size={12} data-id="element-237" /> Acceso Inmediato
                </span>
                <span className="flex items-center gap-1" data-id="element-238">
                  <ShieldCheck size={12} data-id="element-239" /> Garantía Total
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence data-id="element-240">
        {showUpsell && <ExitUpsellPopup onAccept={handleAcceptUpsell} onDecline={handleDeclineUpsell} onClose={() => setShowUpsell(false)} data-id="element-241" />}
      </AnimatePresence>
    </section>;
};
const WhyThisPriceSection = () => <section className="py-12 px-4 bg-[#0d0d0d] text-center" data-id="element-242">
    <div className="max-w-2xl mx-auto" data-id="element-243">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={staggerContainer} data-id="element-244">
        <motion.h3 variants={fadeInUp} className="text-2xl text-white font-bold mb-4" data-id="element-245">
          ¿Por qué este precio?
        </motion.h3>
        <motion.p variants={fadeInUp} className="text-gray-300 text-base md:text-lg leading-relaxed mb-8" data-id="element-246">
          He decidido que el precio sea la menor de tus preocupaciones. Por
          menos de lo que cuesta un combo de hamburguesa, obtienes la llave para
          generar ingresos en dólares mes tras mes. Mi meta es que dejes de ser
          una espectadora y te conviertas en{' '}
          <span className="text-white font-bold" data-id="element-247">dueña de un sistema</span>.
        </motion.p>
      </motion.div>
    </div>
  </section>;
const GuaranteeSection = () => <section className="py-16 px-4 bg-[#080808] border-t border-white/5" data-id="element-248">
    <div className="max-w-2xl mx-auto text-center" data-id="element-249">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={staggerContainer} data-id="element-250">
        <motion.div variants={fadeInUp} className="flex justify-center mb-4" data-id="element-251">
          <ShieldCheck size={40} className="text-[#00e676]" data-id="element-252" />
        </motion.div>

        <motion.h3 variants={fadeInUp} className="text-2xl text-white font-bold mb-4" data-id="element-253">
          Tu inversión es 100% segura
        </motion.h3>

        <motion.p variants={fadeInUp} className="text-gray-300 text-base leading-relaxed mb-6" data-id="element-254">
          Entra, descarga el material y empieza a publicar. Si sientes que este
          nivel de herramientas no es para ti, no hay problema. El sistema está
          diseñado para que{' '}
          <span className="text-white font-bold" data-id="element-255">ejecutes hoy mismo</span>, sin
          excusas.
        </motion.p>

        <motion.div variants={fadeInUp} className="bg-[#1a1a1a] border-l-4 border-[#f5a623] p-4 text-left mb-8 flex gap-3 items-start" data-id="element-256">
          <AlertTriangle className="text-[#f5a623] shrink-0 mt-0.5" size={20} data-id="element-257" />
          <p className="text-white font-medium text-sm md:text-base" data-id="element-258">
            <span className="font-bold" data-id="element-259">ATENCIÓN:</span> No dejes pasar los
            bonos. Estarán incluidos de forma gratuita{' '}
            <span className="font-bold" data-id="element-260">
              solo mientras esta oferta esté activa en tu pantalla.
            </span>
          </p>
        </motion.div>

        <motion.button variants={fadeInUp} onClick={goToCheckout} className="w-full bg-[#00e676] hover:bg-[#00c853] text-black font-bold text-lg py-4 px-6 rounded-xl transition-all border-b-4 border-[#00b248]" data-id="element-261">
          OBTENER ACCESO INSTANTÁNEO POR $6.36
        </motion.button>
      </motion.div>
    </div>
  </section>;
// --- Legal Modal ---
type ModalType = 'terms' | 'privacy' | null;
const termsContent = `TÉRMINOS Y CONDICIONES

Última actualización: 2024

1. ACEPTACIÓN DE LOS TÉRMINOS
Al adquirir y utilizar el producto digital "Faceless Cash: El Método de Clonación Viral" (en adelante, "el Producto"), usted acepta quedar vinculado por estos Términos y Condiciones. Si no está de acuerdo con alguno de estos términos, no utilice el Producto.

2. DESCRIPCIÓN DEL PRODUCTO
El Producto es una guía digital en formato PDF que contiene información educativa sobre estrategias de creación de contenido digital y monetización en plataformas de redes sociales. El contenido es de carácter informativo y educativo únicamente.

3. LICENCIA DE USO
Al adquirir el Producto, se le otorga una licencia personal, no exclusiva e intransferible para usar el contenido únicamente para sus fines personales. Queda estrictamente prohibido:
- Reproducir, distribuir o revender el Producto total o parcialmente.
- Compartir el acceso o el archivo con terceros.
- Utilizar el contenido para crear productos derivados con fines comerciales.

4. PROPIEDAD INTELECTUAL
Todo el contenido del Producto, incluyendo textos, gráficos, logotipos e imágenes, es propiedad exclusiva de Faceless Cash y está protegido por las leyes de propiedad intelectual aplicables.

5. GARANTÍA DE SATISFACCIÓN
Ofrecemos una garantía de devolución de dinero de 30 días. Si aplica las estrategias del Producto y no obtiene resultados, puede solicitar un reembolso completo dentro de los 30 días posteriores a su compra.

6. LIMITACIÓN DE RESPONSABILIDAD
Los resultados descritos en el material de marketing son ejemplos de lo que es posible, no una garantía de resultados. El éxito individual depende de muchos factores, incluyendo el esfuerzo, la dedicación y las condiciones del mercado. Faceless Cash no garantiza ingresos específicos.

7. MODIFICACIONES
Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación.

8. LEY APLICABLE
Estos términos se rigen por las leyes aplicables en la jurisdicción del vendedor. Cualquier disputa será resuelta mediante arbitraje vinculante.`;
const privacyContent = `POLÍTICA DE PRIVACIDAD

Última actualización: 2024

1. INFORMACIÓN QUE RECOPILAMOS
Al realizar una compra, recopilamos la información necesaria para procesar su pedido, incluyendo nombre, dirección de correo electrónico e información de pago. Esta información es procesada de forma segura a través de nuestra plataforma de pago (Shopify).

2. USO DE LA INFORMACIÓN
Utilizamos su información personal para:
- Procesar y completar su compra.
- Enviarle el Producto digital adquirido.
- Enviarle comunicaciones relacionadas con su compra.
- Mejorar nuestros productos y servicios.
- Cumplir con obligaciones legales.

3. PROTECCIÓN DE DATOS
Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Todos los pagos son procesados mediante cifrado SSL.

4. COOKIES Y TECNOLOGÍAS DE SEGUIMIENTO
Nuestro sitio web utiliza cookies y tecnologías similares, incluyendo el Meta Pixel de Facebook, para mejorar su experiencia de navegación y mostrarle publicidad relevante. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.

5. COMPARTIR INFORMACIÓN CON TERCEROS
No vendemos, intercambiamos ni transferimos su información personal a terceros, excepto cuando sea necesario para procesar su compra (procesadores de pago) o cuando lo exija la ley.

6. RETENCIÓN DE DATOS
Conservamos su información personal durante el tiempo necesario para cumplir con los propósitos descritos en esta política, a menos que la ley exija un período de retención más largo.

7. SUS DERECHOS
Usted tiene derecho a:
- Acceder a la información personal que tenemos sobre usted.
- Solicitar la corrección de información inexacta.
- Solicitar la eliminación de su información personal.
- Oponerse al procesamiento de su información.

8. CAMBIOS A ESTA POLÍTICA
Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cambios significativos publicando la nueva política en nuestro sitio web.`;
const LegalModal = ({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) => {
  const isTerms = type === 'terms';
  const title = isTerms ? 'Términos y Condiciones' : 'Política de Privacidad';
  const content = isTerms ? termsContent : privacyContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-262">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-263" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="relative w-full max-w-2xl max-h-[80vh] bg-[#111] border border-[#f5a623]/30 rounded-2xl shadow-[0_0_60px_rgba(245,166,35,0.15)] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-264">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 shrink-0" data-id="element-265">
          <h2 className="text-2xl text-[#f5a623]" data-id="element-266">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors text-gray-400 hover:text-white" data-id="element-267">
            <X size={16} data-id="element-268" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-5 flex-1" data-id="element-269">
          <div className="text-gray-400 text-sm leading-relaxed whitespace-pre-line font-sans" data-id="element-270">
            {content}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 shrink-0" data-id="element-271">
          <button onClick={onClose} className="w-full bg-[#f5a623]/10 hover:bg-[#f5a623]/20 border border-[#f5a623]/30 text-[#f5a623] font-semibold py-2.5 rounded-xl transition-colors text-sm" data-id="element-272">
            Cerrar
          </button>
        </div>
      </motion.div>
    </motion.div>;
};
const Footer = () => {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-black py-10 text-center text-gray-600 text-sm" data-id="element-273">
        <p className="mb-4" data-id="element-274">
          © 2024 Faceless Cash. Todos los derechos reservados.
        </p>
        <div className="flex justify-center gap-6" data-id="element-275">
          <button onClick={() => setActiveModal('terms')} className="hover:text-gray-400 transition-colors cursor-pointer underline-offset-2 hover:underline" data-id="element-276">
            Términos y Condiciones
          </button>
          <button onClick={() => setActiveModal('privacy')} className="hover:text-gray-400 transition-colors cursor-pointer underline-offset-2 hover:underline" data-id="element-277">
            Política de Privacidad
          </button>
        </div>
        <p className="mt-8 text-xs max-w-2xl mx-auto px-4 opacity-50" data-id="element-278">
          Descargo de responsabilidad: Los resultados pueden variar. Este
          producto no garantiza ingresos. El éxito depende de tu dedicación y
          aplicación de las estrategias enseñadas. Este sitio no es parte del
          sitio web de TikTok o ByteDance Ltd. Además, este sitio NO está
          respaldado por TikTok de ninguna manera.
        </p>
      </footer>

      <AnimatePresence data-id="element-279">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-280" />}
      </AnimatePresence>
    </>;
};
export function SalesPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden font-sans selection:bg-[#f5a623] selection:text-black" data-id="element-281">
      <UrgencyBar data-id="element-282" />
      <SalesPopup data-id="element-283" />
      <HeroSection data-id="element-284" />
      <PainPointSection data-id="element-285" />
      <OfferIntroSection data-id="element-286" />
      <MainProductCard data-id="element-287" />
      <BonusesSection data-id="element-288" />
      <SummarySection data-id="element-289" />
      <WhyThisPriceSection data-id="element-290" />
      <GuaranteeSection data-id="element-291" />
      <Footer data-id="element-292" />
    </div>;
}